<template>
  <div class="wrapper">
    <div class="animated fadeIn">
      <b-card>
        <b-row class="mb-3">
          <b-col cols="6" class="mt-1">
            <h4>{{ 'advance_invoices_list_title' | translate('global') }}</h4>
          </b-col>
          <b-col cols="6">
            <div class="pull-right">
              <b-button variant="primary" class="mt-1" :disabled="disabledButtons.create"
                        :to="{ name: 'AdvanceInvoiceCreate' }">
                <i class="fa fa-plus mr-2"></i>{{ 'add_new' | translate('global') }}
              </b-button>
            </div>
          </b-col>
        </b-row>
        <hr>

        <div v-if="spinners.contentIsLoading" class="text-center">
          <b-spinner type="grow" variant="primary"/>
        </div>
        <div v-else>
          <!--------FILTERS---------------------->
          <b-row>
            <b-col cols="6" xl="4">
              <b-form-group>
                <b-form-input type="text"
                              :placeholder="'invoice_number_placeholder' | translate('global')"
                              v-model="filters['number']"/>
              </b-form-group>
            </b-col>
            <b-col cols="6" xl="4">
              <b-form-group>
                <treeselect :default-expand-level="1"
                            :placeholder="'supplier_select' | translate('global')"
                            instanceId="loadSuppliers"
                            :load-options="loadOptions" :async="true" :cacheOptions="false"
                            :searchPromptText="'supplier_autocomplete' | translate('global')"
                            :noResultsText="'no_results_found' | translate('global')"
                            v-model="filters.supplier_id"/>
              </b-form-group>
            </b-col>
            <b-col cols="6" xl="4">
              <b-form-group>
                <treeselect :options="statusesOptions" :default-expand-level="1"
                            :placeholder="'select_status' | translate('global')"
                            v-model="filters.status"/>
              </b-form-group>
            </b-col>

            <b-col cols="12" xl="4">
              <b-form-group>
                <datepicker :placeholder="'select_issue_at_date_range' | translate('global')"
                            v-model="filters.issue_at" range format="DD.MM.YYYY"
                            v-bind:value-type="getServerTimeFormatWithoutTime()"/>
              </b-form-group>
            </b-col>
            <b-col cols="12" xl="4">
              <b-form-group>
                <datepicker :placeholder="'select_created_at_date_range' | translate('global')"
                            v-model="filters.created_at" range format="DD.MM.YYYY"
                            v-bind:value-type="getServerTimeFormatWithoutTime()"/>
              </b-form-group>
            </b-col>
            <b-col cols="12" xl="4">
              <b-form-group>
                <datepicker :placeholder="'select_updated_at_date_range' | translate('global')"
                            v-model="filters.updated_at" range format="DD.MM.YYYY"
                            v-bind:value-type="getServerTimeFormatWithoutTime()"/>
              </b-form-group>
            </b-col>

            <b-col cols="12" class="mb-2">
              <b-button variant="primary" block @click="getData()"
                        :disabled="disabledButtons.filter">
                {{ 'filter' | translate('global') }}
              </b-button>
            </b-col>
          </b-row>
          <!--------END OF FILTERS--------------->

          <b-row>
            <b-col cols="12">
              <EmptyTable v-if="!tableItems.length"/>
              <b-table v-else class="table-sm text-center" responsive bordered
                       :fields="tableFields" :items="tableItems">
                <template v-slot:options="row">
                  <div class="flex-container options-center">
                    <span class="flex-container options-center" v-if="spinners.isSaving">
                      <a v-if="row.item.canReject" class="mr-3">
                        <i class="fa fa-times-circle fa-lg fa-secondary-disabled"/>
                      </a>
                      <a v-if="row.item.canApprove" class="mr-3">
                        <i class="fa fa-check-circle fa-lg fa-secondary-disabled"/>
                      </a>
                    </span>
                    <span class="flex-container options-center" v-else>
                      <a v-if="row.item.canReject && row.item.dbStatus !== 'ADV_INVOICE_PAYMENT_WAIT_ACCOUNTANT_APPROVE'" class="mr-3"
                         @click="updateInvoiceStatus(row.item.id, false)">
                        <i class="fa fa-times-circle fa-lg fa-danger"
                           v-b-tooltip.hover v-bind:title="'reject_payment' | translate('global')"/>
                      </a>
                      <a v-if="row.item.canApprove && row.item.dbStatus !== 'ADV_INVOICE_PAYMENT_WAIT_ACCOUNTANT_APPROVE'" class="mr-3"
                         @click="updateInvoiceStatus(row.item.id, true)">
                        <i class="fa fa-check-circle fa-lg fa-success"
                           v-b-tooltip.hover v-bind:title="'approve_payment' | translate('global')"/>
                      </a>
                    </span>
                    <router-link :to="{ name: 'AdvanceInvoiceUpdate', params:{ id: row.item.id }}">
                      <a><i class="fa fa-edit fa-lg"/></a>
                    </router-link>
                  </div>
                </template>
              </b-table>
            </b-col>
          </b-row>
          <b-row>
            <!--Paginator-->
            <b-col cols="12" v-if="tableItems.length">
              <Pagination :data="listPaginator" :limit="5" @pagination-change-page="onPageChange"/>
            </b-col>
          </b-row>
        </div>
      </b-card>
      <Modal ref="approve"
             @onClose="closeModal"
             @onHidden="hideModal"
             :values="modalValues">
        <template slot="body">
          <div class="modal-body">
            <b-row>
              <b-col cols="12">
                <span v-if="modalIsApprove">{{ 'order_approve_invoice' | translate('global') }}</span>
                <span v-else>{{ 'order_reject_invoice' | translate('global') }}</span>
                <b-form-group>
                  <label>{{ 'comment' | translate('global') }}</label>
                  <b-form-textarea type="text" v-model="modalComment"
                                   :placeholder="'advance_invoice_modal_comment_placeholder' | translate('global')"/>
                </b-form-group>
              </b-col>
            </b-row>
          </div>
        </template>
        <template slot="footer">
          <b-button variant="primary" data-dismiss="modal" :disabled="spinners.isSending" @click='onSend()'>
            <b-spinner v-if="spinners.isSending" type="grow" small/>
            {{ 'send' | translate('global') }}
          </b-button>
          <b-button variant="secondary" @click="closeModal()" class="ml-2">
            {{ 'cancel' | translate('global') }}
          </b-button>
        </template>
      </Modal>
    </div>
  </div>
</template>

<script>
import statuses from '@/store/statuses';
import { mapGetters, mapActions } from 'vuex';
import Pagination from 'laravel-vue-pagination';
import VuexTypes from '@/store/types/index';
import Services from '@/services/index';
import forEach from 'lodash.foreach';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import TreeselectMixin from '@/mixins/TreeselectMixin';
import ServerTimeFormatMixin from '@/mixins/ServerTimeFormatMixin';
import Datepicker from 'vue2-datepicker';
import 'vue2-datepicker/locale/lv';
import EmptyTable from '@/views/sharedComponents/EmptyTableData';
import Modal from '@/views/sharedComponents/Modal';

const InvoicesAdvanceTypes = VuexTypes.invoicesAdvance;
const SuppliersService = Services.suppliers;
export default {
  mounted() {
    this.getData();
  },
  props: ['page', 'viewPending'],
  mixins: [TreeselectMixin, ServerTimeFormatMixin],
  data() {
    return {
      spinners: {
        contentIsLoading: true,
        isSaving: false
      },
      disabledButtons: {
        filter: false,
        create: true
      },
      tableFields: [
        { key: 'number', label: this.$t('invoice_number', 'global') },
        { key: 'issue_at', label: this.$t('issue_at', 'global') },
        { key: 'created_at', label: this.$t('created_at', 'global') },
        { key: 'updated_at', label: this.$t('updated_at', 'global') },
        { key: 'supplier', label: this.$t('supplier', 'global') },
        { key: 'status', label: this.$t('status', 'global') },
        { key: 'sum', label: this.$t('sum', 'global') },
        { key: 'options', label: this.$t('options', 'global') }
      ],
      filters: {
        number: null,
        supplier_id: null,
        status: null,
        issue_at: null,
        created_at: null,
        updated_at: null,
      },
      tableItems: [],
      statusesOptions: [],
      modalValues: {
        id: 'send-advance-approve-decline-modal',
        title: this.$t('are_you_sure', 'global'),
      },
      modalIsApprove: null,
      modalComment: ''
    };
  },
  components: {
    Pagination, Treeselect, Datepicker, EmptyTable, Modal
  },
  computed: {
    ...mapGetters('InvoicesAdvance', [
      'advanceInvoicesList',
      'advanceInvoicesListFilters',
      'advanceInvoicePaymentApprove',
      'advanceInvoicePaymentReject',
    ]),
    listPaginator() {
      return this.advanceInvoicesList.data.meta;
    },
    list() {
      return this.advanceInvoicesList;
    },
    params() {
      const data = {};
      forEach(this.filters, (value, param) => {
        if (value) {
          if (param === 'issue_at' || param === 'created_at' || param === 'updated_at') {
            data[`${param}[from]`] = value[0];
            data[`${param}[to]`] = value[1];
          } else {
            data[param] = value;
          }
        }
      });
      data['page'] = this.page;
      if (this.viewPending === '1') {
        data['view_pending'] = 1;
      }
      return data;
    },
  },
  watch: {
    page: 'getData',
    listPaginator: {
      handler: function () {
        if (this.listPaginator && this.listPaginator.last_page < this.page) {
          this.onPageChange(this.listPaginator.last_page);
        }
      },
      deep: true
    },
    list: {
      handler: function () {
        if (this.list['status'] === statuses.LOADED) {
          this.spinners.contentIsLoading = false;
          this.fillTableData();

          const data = this.list.data;
          const actions = data ? data['actions'] : data['actions'];
          this.disabledButtons.create = !actions['create'];
        }
      },
      deep: true
    },
    advanceInvoicesListFilters: {

      handler: function () {
        if (this.advanceInvoicesListFilters['status'] === statuses.LOADED) {
          this.fillFilterData();
        }
      },
      deep: true
    },
    advanceInvoicePaymentApprove: {
      handler: function () {
        if (this.advanceInvoicePaymentApprove.status === statuses.LOADED) {
          this.closeModal();
          this.alertSuccess();
          this.getData();
        } else if (this.advanceInvoicePaymentApprove.status === statuses.ERROR) {
          this.alertError();
        }
        this.spinners.isSending = false;
      },
      deep: true
    },
    advanceInvoicePaymentReject: {
      handler: function () {
        if (this.advanceInvoicePaymentReject.status === statuses.LOADED) {
          this.closeModal();
          this.alertSuccess();
          this.getData();
        } else if (this.advanceInvoicePaymentReject.status === statuses.ERROR) {
          this.alertError();
        }
        this.spinners.isSending = false;
      },
      deep: true
    }
  },
  methods: {
    ...mapActions('InvoicesAdvance', [
      InvoicesAdvanceTypes.ADV_INVOICES_LIST,
      InvoicesAdvanceTypes.ADV_INVOICES_LIST_FILTERS,
      InvoicesAdvanceTypes.ADV_INVOICE_PAYMENT_APPROVE,
      InvoicesAdvanceTypes.ADV_INVOICE_PAYMENT_REJECT,
    ]),
    getData() {
      this.ADV_INVOICES_LIST(this.params);
      this.ADV_INVOICES_LIST_FILTERS();
    },
    fillTableData() {
      const result = [];
      forEach(this.list.data.data, (item) => {
        result.push({
          id: item['id'],
          number: item['number'],
          issue_at: item['issue_at'],
          created_at: item['created_at'],
          updated_at: item['updated_at'],
          supplier: item['supplier'] ? item['supplier']['name'] : '-',
          status: this.$t(item['status'], 'statuses'),
          sum: item['sum'],
          canApprove: item['actions']['approve_payment'],
          canReject: item['actions']['decline_payment'],
          dbStatus: item['status']
        });
      });

      this.tableItems = result;
    },
    fillFilterData() {
      const invoiceStatuses = [];
      forEach(this.advanceInvoicesListFilters.data['statuses'], (status) => {
        invoiceStatuses.push({
          id: status,
          label: this.$t(status, 'statuses'),
        });
      });
      this.statusesOptions = invoiceStatuses;
    },
    onPageChange(page) {
      if (!page) {
        page = this.page;
      }
      if (page !== this.page) {
        this.$router.push({ name: 'AdvanceInvoices', params: { page } });
      }
    },
    updateInvoiceStatus(id, approve) {
      this.spinners.isSaving = id;
      this.modalIsApprove = approve;
      this.$refs['approve'].show();
    },
    closeModal() {
      this.$refs['approve'].hide();
      this.modalIsApprove = null;
      this.modalComment = '';
      this.spinners.isSaving = false;
    },
    hideModal() {
      this.modalIsApprove = null;
      this.modalComment = '';
      this.spinners.isSaving = false;
    },
    onSend() {
      this.spinners.isSending = true;

      if (this.modalIsApprove) {
        this.ADV_INVOICE_PAYMENT_APPROVE({
          invoiceId: this.spinners.isSaving,
          approvalData: {
            comment: this.modalComment
          }
        });
      } else {
        this.ADV_INVOICE_PAYMENT_REJECT({
          invoiceId: this.spinners.isSaving,
          rejectData: {
            comment: this.modalComment
          }
        });
      }
    },
    async loadOptions({ action, searchQuery, callback, instanceId }) {
      let options = [];
      const query = searchQuery.trim();
      if (action === 'ASYNC_SEARCH') {
        const valid = this.checkSearchQuery(query);
        if (valid) {
          options = await this[instanceId](query);
        }
      }
      callback(null, options);
    },
    async loadSuppliers(name) {
      const param = name || '';
      const data = await SuppliersService.search(param);
      const options = [];
      forEach(data.data.data, (item) => {
        options.push({
          id: item['id'],
          label: item['name']
        });
      });
      return options;
    },
  }
};
</script>

<style scoped>
  .options-center {
    justify-content: center
  }
</style>
